import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MealAutocomplete } from "@/components/MealAutocomplete";
import { api, errMsg } from "@/lib/api";

const foldName = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

export const MealReplaceDialog = ({ open, onOpenChange, meal, onSubmit, busy }) => {
  const [name, setName] = useState("");
  const [picked, setPicked] = useState(null);
  const [macros, setMacros] = useState({ kcal: "", protein: "", carbs: "", fat: "", notes: "" });
  const [items, setItems] = useState([]);
  const [estimating, setEstimating] = useState(false);
  const [estimateHint, setEstimateHint] = useState("");

  useEffect(() => {
    if (!open || !meal) return;
    setName("");
    setPicked(null);
    setEstimateHint("");
    setMacros({
      kcal: String(meal.kcal || ""),
      protein: String(meal.protein || ""),
      carbs: String(meal.carbs || ""),
      fat: String(meal.fat || ""),
      notes: "",
    });
    api.get("/nutrition/meal-suggestions", { params: { slot: meal.slot } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [open, meal]);

  const applyPick = (item) => {
    setPicked(item);
    if (!item) {
      setEstimateHint("Plato nuevo: calcula macros o ajústalos a mano. Se guardará en tus opciones.");
      return;
    }
    setMacros((p) => ({
      ...p,
      kcal: item.kcal != null ? String(item.kcal) : p.kcal,
      protein: item.protein != null ? String(item.protein) : p.protein,
      carbs: item.carbs != null ? String(item.carbs) : p.carbs,
      fat: item.fat != null ? String(item.fat) : p.fat,
      notes: item.notes || p.notes,
    }));
    setEstimateHint(
      item.source === "personal"
        ? "Rellenado con un plato que ya registraste. Puedes ajustar antes de guardar."
        : "Rellenado desde el catálogo. Puedes ajustar si la ración fue distinta.",
    );
  };

  const estimateMacros = async () => {
    const n = name.trim();
    if (n.length < 2) return toast.error("Escribe primero el nombre del plato");
    setEstimating(true);
    setEstimateHint("");
    try {
      const { data } = await api.post("/nutrition/estimate-macros", { name: n, slot: meal?.slot, use_ai: true });
      setMacros((p) => ({
        ...p,
        kcal: String(data.kcal),
        protein: String(data.protein),
        carbs: String(data.carbs),
        fat: String(data.fat),
      }));
      setPicked(null);
      const src = data.source === "ai" ? "IA" : data.source === "catalog" ? "catálogo" : "estimación";
      setEstimateHint(`Sugerido (${src}${data.matched ? `: ${data.matched}` : ""}). Puedes ajustar los valores.`);
      toast.success("Macros sugeridos — revísalos si quieres");
    } catch (e) { toast.error(errMsg(e)); } finally { setEstimating(false); }
  };

  const submit = () => {
    const n = name.trim();
    if (!n) return toast.error("Indica qué comiste");
    const notes = macros.notes?.trim() || undefined;
    const catalogHit = picked?.source === "catalog"
      ? picked
      : items.find((it) => it.source === "catalog" && foldName(it.name) === foldName(n));
    const macrosUntouched = catalogHit
      && String(catalogHit.kcal ?? "") === String(macros.kcal)
      && String(catalogHit.protein ?? "") === String(macros.protein)
      && String(catalogHit.carbs ?? "") === String(macros.carbs)
      && String(catalogHit.fat ?? "") === String(macros.fat);
    if (picked?.source !== "personal" && catalogHit && macrosUntouched) {
      onSubmit({ meal_name: catalogHit.name, notes });
      return;
    }
    onSubmit({
      custom_name: n,
      custom_kcal: macros.kcal ? Number(macros.kcal) : undefined,
      custom_protein: macros.protein ? Number(macros.protein) : undefined,
      custom_carbs: macros.carbs ? Number(macros.carbs) : undefined,
      custom_fat: macros.fat ? Number(macros.fat) : undefined,
      notes,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-1.5rem)] min-w-0 overflow-x-hidden overflow-y-auto rounded-3xl sm:max-w-md" data-testid="meal-replace-dialog">
        <DialogHeader className="pr-6">
          <DialogTitle className="font-display text-xl">¿Qué comiste de verdad?</DialogTitle>
          <DialogDescription className="break-words">
            Sustituye «{meal?.name}» ({meal?.label}). Busca en tus platos o en el catálogo; si es nuevo, escríbelo y se guardará para la próxima.
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0 space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-stone-600">Plato</label>
            <MealAutocomplete
              value={name}
              onChange={(v) => { setName(v); setEstimateHint(""); }}
              onPick={applyPick}
              items={items}
              placeholder="Ej.: tostada con jamón, bowl de…"
              testid="meal-replace-autocomplete"
            />
          </div>
          <button
            type="button"
            disabled={estimating || name.trim().length < 2}
            onClick={estimateMacros}
            data-testid="meal-estimate-macros-btn"
            className="w-full rounded-full border border-food/40 bg-food-soft py-2.5 text-xs font-semibold text-food-ink transition-transform active:scale-95 disabled:opacity-50"
          >
            {estimating ? "Calculando…" : "Calcular macros aprox."}
          </button>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-stone-600">Calorías (kcal)</label>
              <Input type="number" inputMode="numeric" value={macros.kcal} onChange={(e) => setMacros((p) => ({ ...p, kcal: e.target.value }))} data-testid="meal-replace-custom-kcal" className="rounded-2xl" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-stone-600">Proteína (g)</label>
              <Input type="number" inputMode="numeric" value={macros.protein} onChange={(e) => setMacros((p) => ({ ...p, protein: e.target.value }))} data-testid="meal-replace-custom-protein" className="rounded-2xl" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-stone-600">Hidratos (g)</label>
              <Input type="number" inputMode="numeric" value={macros.carbs} onChange={(e) => setMacros((p) => ({ ...p, carbs: e.target.value }))} data-testid="meal-replace-custom-carbs" className="rounded-2xl" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-stone-600">Grasas (g)</label>
              <Input type="number" inputMode="numeric" value={macros.fat} onChange={(e) => setMacros((p) => ({ ...p, fat: e.target.value }))} data-testid="meal-replace-custom-fat" className="rounded-2xl" />
            </div>
          </div>
          <p className="text-xs text-stone-500" data-testid="meal-replace-hint">
            {estimateHint || "Empieza a escribir: aparecerán tus platos y el catálogo. Al elegir uno se rellenan los macros."}
          </p>
          <Textarea placeholder="Notas (opcional)" value={macros.notes} onChange={(e) => setMacros((p) => ({ ...p, notes: e.target.value }))} className="max-w-full rounded-2xl" data-testid="meal-replace-notes" />
          <button disabled={busy} onClick={submit} data-testid="meal-replace-submit" className="w-full rounded-full bg-food py-3.5 font-semibold text-white transition-transform active:scale-95 disabled:opacity-60">
            {busy ? "Guardando…" : "Guardar lo que comí"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
